import { createTheme } from '@mui/material/styles';
import '../types/theme.d.ts'; // Import your custom type declarations

// ☀️ LIGHT THEME (Solar Soft Inspired)
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2563EB', // Azul vibrante (mantém consistência com dark)
      '01': '#2563EB',
      '02': '#60A5FA', // Azul claro para hover e highlights
      '03': '#E0ECFF', // Fundo de componentes azuis
      '04': '#1E40AF',
    },
    secondary: {
      main: '#06B6D4', // Aqua/ciano (link com dark)
      '01': '#06B6D4',
      '02': '#67E8F9',
      '03': '#FACC15',
      '04': '#0891B2',
    },
    tertiary: {
      main: '#FACC15', // Amarelo suave (inspiração da 2ª imagem)
      '01': '#60A5FA',
      '02': '#2563EB',
      '03': '#FCD34D',
      '04': '#CA8A04',
    },
    text: {
      primary: '#1F1F1F', // Preto suave
      secondary: '#4A4A4A', // Cinza escuro
    },
    grey: {
      '01': '#9E9E9E', // Placeholder
      '02': '#E5E7EB', // Bordas leves
      '03': '#F3F4F6', // Fundo de inputs
      '04': '#2563EB', // Fundo ainda mais claro
      '05': '#D1D5DB', // Hover leve
    },
    white: {
      main: '#F2F3F5', // background
      '01': '#FFFFFF', // Cards
      '02': '#F3F4F6',
      '03': '#F9F7F2',
      '04': '#FFFFFF',
      '05': '#FFFFFF',
      '06': '#000000',
    },
    transparent: {
      main: 'rgba(0, 207, 255, 0.4)',
      secondary: 'rgba(0,0,0,0.08)',
      tertiary: 'rgba(255, 255, 255, 0.08)',
      quaternary: 'rgba(0, 0, 0, 0.05)',
    },
    status: {
      online: '#4CAF50',
      offline: '#F44336',
      warning: '#F59E0B',
      enabled: '#7C5CFF',
      disabled: '#828a99',
      info: '#0EA5E9',
      stats: {
        min: '#4ade80',
        max: '#f87171',
        avg: '#7dd3fc',
        deviation: '#facc15',
      },
    },
    traceroute: {
      good: '#22C55E',
      attention: '#EAB308',
      partial: '#F97316',
      failed: '#EF4444',
    },
    fixed: { white: '#FFFFFF', black: '#000000', grey: '#adadad' },
  },
  typography: {
    fontFamily: 'Poppins, sans-serif',
    h1: {
      fontSize: '32px',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '0.5px',
      color: '#1F1F1F',
    },
    h2: {
      fontSize: '28px',
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '0.5px',
      color: '#1F1F1F',
    },
    h3: {
      fontSize: '24px',
      fontWeight: 600,
      lineHeight: 1.25,
      letterSpacing: '0.5px',
      color: '#1F1F1F',
    },
    h4: {
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '0.5px',
      color: '#1F1F1F',
    },
    h5: {
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '0.5px',
      color: '#1F1F1F',
    },
    h6: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '0.5px',
      color: '#1F1F1F',
    },
    subtitle1: {
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: 1.4,
      color: '#4A4A4A',
    },
    subtitle2: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: 1.4,
      color: '#4A4A4A',
    },
    body1: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#1F1F1F',
    },
    body2: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#4A4A4A',
    },
    caption: {
      fontSize: '12px',
      fontWeight: 400,
      fontStyle: 'italic',
      color: '#9E9E9E',
    },
    overline: {
      fontSize: '10px',
      fontWeight: 500,
      textTransform: 'uppercase',
      color: '#9E9E9E',
    },
  },
  buttonTheme: {
    default: {
      background: '#2563EB',
      color: '#FFFFFF',
      border: '1px solid #2563EB',
      hover: '#1D4ED8',
      borderRadius: '32px',
      padding: '6px 24px',
    },
    primary: {
      background: '#2563EB',
      color: '#FFFFFF',
      hover: '#1E40AF',
      borderRadius: '32px',
    },
    secondary: {
      background: '#FACC15',
      color: '#000000',
      border: '1px solid #FCD34D',
      hover: '#FBBF24',
      borderRadius: '32px',
      padding: '6px 24px',
    },
    tertiary: {
      background: '#FACC15',
      color: '#000000',
      border: '1px solid #FCD34D',
      hover: '#FBBF24',
      borderRadius: '32px',
      padding: '6px 24px',
    },
    outlined: {
      backgroundColor: 'transparent',
      color: '#2563EB',
      border: '1px solid #2563EB',
      hover: '#60A5FA',
      colorHover: '#1F1F1F',
      borderRadius: '32px',
      padding: '6px 24px',
    },
    red: {
      background: '#FF4C4C',
      color: '#ffffff',
      border: '1px solid #EF4444',
      hover: '#DC2626',
      borderRadius: '32px',
      padding: '6px 24px',
    },
  },
});

// 🌙 DARK THEME (Deep Space Neon)
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00CFFF',
      '01': '#00CFFF',
      '02': '#4cb7cf',
      '03': '#35b8f5',
      '04': '#00aeff',
    },
    secondary: {
      main: '#FFD300', // Amarelo Neon
      '01': '#FFD300',
      '02': '#FFF642',
      '03': '#FFEA00',
      '04': '#FFB84C',
    },
    tertiary: {
      main: '#00E0FF',
      '01': '#003B5C',
      '02': '#112240',
      '03': '#1A1F2E',
      '04': '#0A0F1F',
    },
    text: { primary: '#E0F7FF', secondary: '#FFD300' },
    grey: {
      '01': '#4D6A8A',
      '02': '#294866',
      '03': '#1E293B',
      '04': '#101827',
      '05': '#ffffff3b',
    },
    white: {
      main: '#0A0F1F',
      '01': '#1C2230',
      '02': '#1E273B',
      '03': '#0A0F1F',
      '04': '#0A0F1F',
      '05': '#0D1117',
      '06': '#FFFFFF',
    },
    transparent: {
      main: 'rgba(0, 207, 255, 0.4)',
      secondary: 'rgba(0,0,0,0.08)',
      tertiary: 'rgba(255, 255, 255, 0.08)',
      quaternary: 'rgba(0, 0, 0, 0.05)',
    },
    status: {
      online: '#0FCC45',
      offline: '#FF4C4C',
      warning: '#FFD300',
      enabled: '#5934ed',
      disabled: '#AAB4C6',
      info: '#00CFFF',
      stats: {
        min: '#4ade80',
        max: '#f87171',
        avg: '#7dd3fc',
        deviation: '#facc15',
      },
    },
    traceroute: {
      good: '#0FCC45',
      attention: '#EAB308',
      partial: '#FF8A00',
      failed: '#FF4C4C',
    },
    fixed: { white: '#FFFFFF', black: '#000000', grey: '#adadad' },
  },
  typography: {
    fontFamily: 'Poppins, sans-serif',
    h1: {
      fontSize: '32px',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '0.5px',
      color: '#E0F7FF',
    },
    h2: {
      fontSize: '28px',
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '0.5px',
      color: '#E0F7FF',
    },
    h3: {
      fontSize: '24px',
      fontWeight: 600,
      lineHeight: 1.25,
      letterSpacing: '0.5px',
      color: '#E0F7FF',
    },
    h4: {
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '0.5px',
      color: '#E0F7FF',
    },
    h5: {
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '0.5px',
      color: '#E0F7FF',
    },
    h6: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '0.5px',
      color: '#E0F7FF',
    },
    subtitle1: {
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: 1.4,
      color: '#FFD300',
    },
    subtitle2: {
      fontSize: '10px',
      fontWeight: 500,
      lineHeight: 1.4,
      color: '#c70000',
      marginTop: '0px',
      marginBottom: '0px',
    },
    body1: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#E0F7FF',
    },
    body2: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#7FDBFF',
    },
    caption: {
      fontSize: '12px',
      fontWeight: 400,
      fontStyle: 'italic',
      color: '#7FDBFF',
    },
    overline: {
      fontSize: '10px',
      fontWeight: 500,
      textTransform: 'uppercase',
      color: '#FFD300',
    },
  },
  buttonTheme: {
    default: {
      background: '#00CFFF',
      color: '#000000',
      border: '1px solid #00CFFF',
      hover: '#00B2E0',
      borderRadius: '32px',
      padding: '6px 24px',
    },
    primary: {
      background: '#00CFFF',
      color: '#163A52',
      hover: '#00A3D6',
      borderRadius: '32px',
    },
    secondary: {
      background: '#FFD300',
      color: '#000000',
      border: '1px solid #FFD300',
      hover: '#FFF642',
      borderRadius: '32px',
      padding: '6px 24px',
    },
    tertiary: {
      background: '#00E0FF',
      color: '#000000',
      border: '1px solid #00CFFF',
      hover: '#00A3D6',
      borderRadius: '32px',
      padding: '6px 24px',
    },
    outlined: {
      backgroundColor: 'transparent',
      color: '#FFD300',
      border: '1px solid #FFD300',
      hover: '#000000',
      borderRadius: '32px',
      padding: '6px 24px',
    },
    red: {
      background: '#c70000',
      color: '#ffffff',
      border: '1px solid #c70000',
      hover: '#e00000',
      borderRadius: '32px',
      padding: '6px 24px',
    },
  },
});
